/**
 * TechDetail Design System — shared aesthetic for technical detail panels site-wide.
 * 
 * Usage:
 *   import { td } from '../styles/TechDetailStyles';
 *   <div style={td.panel}>...</div>
 *   <div style={td.sectionTitle}>Oscillators</div>
 *   <span style={td.monoValue}>264 Hz</span>
 */

import React from 'react';

// ─── Color palette ───
const colors = {
  bg:           'rgba(9,9,11,0.90)',
  border:       (accent: string) => `${accent}33`,
  text:         '#d4d4d8',
  textDim:      '#a1a1aa',
  textMuted:    '#71717a',
  textFaint:    '#52525b',
  heading:      '#fafafa',
  accent:       '#22d3ee',
  accentDim:    'rgba(34,211,238,0.08)',
  gold:         '#facc15',
  rose:         '#fb7185',
  purple:       '#a78bfa',
  panelBg:      'rgba(255,255,255,0.02)',
  panelBgAlt:   'rgba(255,255,255,0.04)',
  codeBg:       'rgba(34,211,238,0.06)',
  borderLight:  '#27272a',
};

// ─── Shared style objects ───

/** Main panel container — semi-transparent dark glass */
export const tdPanel: React.CSSProperties = {
  background: colors.bg,
  backdropFilter: 'blur(12px)',
  border: `1px solid ${colors.borderLight}`,
  borderRadius: 10,
  padding: '8px 12px',
  fontFamily: 'system-ui, sans-serif',
  color: colors.text,
  fontSize: 11,
  overflow: 'hidden',
  transition: 'max-height 0.3s ease',
};

/** Section title — uppercase, letter-spaced, bottom border */
export const tdSectionTitle: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 700,
  color: colors.textMuted,
  textTransform: 'uppercase',
  letterSpacing: 1.5,
  marginBottom: 6,
  borderBottom: `1px solid ${colors.borderLight}`,
  paddingBottom: 3,
};

/** Collapsible panel with max-height transition */
export function tdCollapsible(expanded: boolean, collapsedH: number, expandedH: number): React.CSSProperties {
  return {
    maxHeight: expanded ? expandedH : collapsedH,
    overflow: 'hidden',
    transition: 'max-height 0.3s ease',
  };
}

/** Section wrapper with bottom margin */
export const tdSection: React.CSSProperties = {
  marginBottom: 10,
};

/** Header strip row — clickable, flex */
export const tdHeaderRow: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  cursor: 'pointer',
};

/** Glyph span */
export const tdGlyph: React.CSSProperties = {
  fontSize: 18,
};

/** Pattern name heading */
export const tdName: React.CSSProperties = {
  fontSize: 12,
  fontWeight: 700,
  color: colors.heading,
  whiteSpace: 'nowrap',
};

/** Category badge pill */
export function tdCategoryBadge(color: string): React.CSSProperties {
  return {
    fontSize: 9,
    color,
    border: `1px solid ${color}44`,
    borderRadius: 4,
    padding: '1px 5px',
  };
}

/** Archetype / subtitle */
export const tdSubtitle: React.CSSProperties = {
  fontSize: 10,
  color: colors.textMuted,
  fontStyle: 'italic',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  flex: 1,
};

/** Quick stat string */
export const tdQuickStats: React.CSSProperties = {
  fontSize: 9,
  color: colors.textFaint,
  whiteSpace: 'nowrap',
};

/** Mono label-value row */
export const tdMonoRow: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  fontSize: 11,
  marginBottom: 1,
};

/** Mono label */
export const tdMonoLabel: React.CSSProperties = {
  color: colors.textMuted,
};

/** Mono value */
export const tdMonoValue: React.CSSProperties = {
  fontFamily: 'monospace',
  color: colors.text,
  fontWeight: 500,
};

/** Formula equation block */
export const tdFormulaEq: React.CSSProperties = {
  fontFamily: 'monospace',
  fontSize: 11,
  color: colors.accent,
  background: colors.accentDim,
  padding: '3px 8px',
  borderRadius: 4,
  display: 'inline-block',
  marginBottom: 2,
};

/** Formula description */
export const tdFormulaDesc: React.CSSProperties = {
  fontSize: 10,
  color: colors.textMuted,
};

/** Tab button */
export function tdTabButton(active: boolean): React.CSSProperties {
  return {
    fontSize: 10,
    fontWeight: active ? 700 : 400,
    color: active ? colors.heading : colors.textMuted,
    background: active ? 'rgba(255,255,255,0.08)' : 'transparent',
    border: 'none',
    cursor: 'pointer',
    padding: '4px 10px',
    borderRadius: 4,
    textTransform: 'uppercase' as const,
    letterSpacing: 1,
  };
}

/** Tab bar */
export const tdTabBar: React.CSSProperties = {
  display: 'flex',
  gap: 4,
  marginTop: 8,
  marginBottom: 6,
};

/** Oscillator row */
export const tdOscRow: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  padding: '2px 6px',
  borderRadius: 4,
  fontSize: 10,
  fontFamily: 'monospace',
};

/** Oscillator index */
export const tdOscIdx: React.CSSProperties = {
  color: colors.textFaint,
  width: 18,
  textAlign: 'right',
};

/** Oscillator frequency */
export const tdOscFreq: React.CSSProperties = {
  color: colors.gold,
  width: 52,
  fontWeight: 600,
};

/** Oscillator amplitude */
export const tdOscAmp: React.CSSProperties = {
  color: colors.text,
  width: 28,
};

/** Oscillator waveform */
export const tdOscWave: React.CSSProperties = {
  color: colors.purple,
  width: 42,
};

/** Oscillator omega */
export const tdOscOmega: React.CSSProperties = {
  color: colors.accent,
  width: 56,
};

/** Oscillator wavenumber */
export const tdOscK: React.CSSProperties = {
  color: colors.rose,
  width: 56,
};

/** Oscillator mode numbers */
export const tdOscMode: React.CSSProperties = {
  color: colors.textMuted,
  width: 52,
};

/** Oscillator LFO */
export const tdOscLfo: React.CSSProperties = {
  color: colors.textDim,
};

/** Scrollable content area */
export const tdScroll: React.CSSProperties = {
  maxHeight: 200,
  overflowY: 'auto',
};

/** Description text block */
export const tdDescription: React.CSSProperties = {
  fontSize: 10,
  color: colors.textDim,
  marginTop: 8,
  padding: '4px 8px',
  background: 'rgba(255,255,255,0.03)',
  borderRadius: 6,
  lineHeight: 1.5,
};

/** Summary row */
export const tdSummaryRow: React.CSSProperties = {
  display: 'flex',
  gap: 12,
  flexWrap: 'wrap',
  marginTop: 8,
  marginBottom: 2,
};

/** Code block */
export const tdCode: React.CSSProperties = {
  fontFamily: 'monospace',
  fontSize: 10,
  color: colors.text,
  background: colors.codeBg,
  borderRadius: 6,
  padding: '8px 10px',
  lineHeight: 1.8,
  marginBottom: 8,
};

/** Narrative text */
export const tdNarrative: React.CSSProperties = {
  fontSize: 10,
  color: colors.textDim,
  lineHeight: 1.6,
};

/** Inline code snippet */
export const tdInlineCode: React.CSSProperties = {
  fontFamily: 'monospace',
  fontSize: 10,
  color: colors.accent,
  background: colors.codeBg,
  padding: '4px 8px',
  borderRadius: 4,
  marginTop: 4,
};

/** Footnote */
export const tdFootnote: React.CSSProperties = {
  fontSize: 9,
  color: colors.textFaint,
  marginTop: 4,
};

/** Expand/collapse button */
export const tdToggleBtn: React.CSSProperties = {
  background: 'none',
  border: 'none',
  color: colors.textFaint,
  cursor: 'pointer',
  fontSize: 12,
  padding: 2,
  lineHeight: 1,
};

/** Close button */
export const tdCloseBtn: React.CSSProperties = {
  background: 'none',
  border: 'none',
  color: '#3f3f46',
  cursor: 'pointer',
  fontSize: 12,
  padding: 2,
  lineHeight: 1,
};

// ─── Category accent colors ───

export const CATEGORY_COLORS: Record<string, string> = {
  Sacred:     '#84cc16',
  Geometric:  '#facc15',
  Chladni:    '#9ca3af',
  Solfeggio:  '#fb7185',
  Natural:    '#38bdf8',
  Audio:      '#22d3ee',
  Meditation: '#a78bfa',
  Numerology: '#fb923c',
  Astrology:  '#818cf8',
  Profile:    '#f472b6',
  Default:    '#8b5cf6',
};

// ─── Helper components ───

interface MonoLabelProps {
  label: string;
  value: string | number;
  dim?: boolean;
}

export const MonoLabel: React.FC<MonoLabelProps> = ({ label, value, dim }) => (
  <div style={tdMonoRow}>
    <span style={{ ...tdMonoLabel, ...(dim ? { color: colors.textFaint } : {}) }}>{label}</span>
    <span style={{ ...tdMonoValue, ...(dim ? { color: colors.textFaint } : {}) }}>{value}</span>
  </div>
);

interface FormulaRowProps {
  eq: string;
  desc: string;
}

export const FormulaRow: React.FC<FormulaRowProps> = ({ eq, desc }) => (
  <div style={{ marginBottom: 5 }}>
    <div style={tdFormulaEq}>{eq}</div>
    <div style={tdFormulaDesc}>{desc}</div>
  </div>
);

interface SectionProps {
  title: string;
  children: React.ReactNode;
}

export const DetailSection: React.FC<SectionProps> = ({ title, children }) => (
  <div style={tdSection}>
    <div style={tdSectionTitle}>{title}</div>
    {children}
  </div>
);
